import { createContext, useContext, useEffect, useState } from "react";
import { useListTimetables } from "@workspace/api-client-react";

interface DiaryContextValue {
  activeTimetableId: number | null;
  setActiveTimetableId: (id: number) => void;
}

const DiaryContext = createContext<DiaryContextValue>({
  activeTimetableId: null,
  setActiveTimetableId: () => {},
});

const STORAGE_KEY = "classtrack:activeTimetableId";

export function DiaryProvider({ children }: { children: React.ReactNode }) {
  const { data: timetables } = useListTimetables();
  const [activeTimetableId, setActiveTimetableIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored) : null;
  });

  // If stored ID doesn't exist in the list (deleted), fall back to first active
  useEffect(() => {
    if (!timetables || timetables.length === 0) return;
    const active = timetables.filter((t: any) => !t.archived);
    if (activeTimetableId === null) {
      const first = active[0];
      if (first) {
        setActiveTimetableIdState(first.id);
        localStorage.setItem(STORAGE_KEY, String(first.id));
      }
      return;
    }
    // Only treat the stored ID as valid if the timetable is still active
    // (not archived). An archived timetable that is still in the list
    // would otherwise be silently kept as the default.
    const exists = active.some((t: any) => t.id === activeTimetableId);
    if (!exists) {
      const first = active[0];
      const newId = first?.id ?? null;
      setActiveTimetableIdState(newId);
      if (newId) localStorage.setItem(STORAGE_KEY, String(newId));
      else localStorage.removeItem(STORAGE_KEY);
    }
  }, [timetables, activeTimetableId]);

  const setActiveTimetableId = (id: number) => {
    setActiveTimetableIdState(id);
    localStorage.setItem(STORAGE_KEY, String(id));
  };

  return (
    <DiaryContext.Provider value={{ activeTimetableId, setActiveTimetableId }}>
      {children}
    </DiaryContext.Provider>
  );
}

export function useDiary() {
  return useContext(DiaryContext);
}
